/**
 * Smart Document Reader route gate (T-163-001 wiring, US-163).
 *
 * Same contract as EvidenceSafeRouteGate (T-162-001 wiring): evaluates the
 * tier gate BEFORE rendering the feature — allowed renders children, denied
 * renders the shared FeatureUpgradeModal (T-137/ROI, T-163-002) instead of
 * the feature. Fail-closed while loading and on errors.
 */
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import FeatureUpgradeModal from '../../components/Shared/FeatureUpgradeModal';
import {
    checkSmartReaderAccess,
    evaluateSmartReaderGate,
    SMART_READER_ROUTES,
    SmartReaderGateResponse,
    SmartReaderFeature,
} from './guard';

interface SmartReaderRouteGateProps {
    children: React.ReactNode;
    /** Test seam — overrides the live tier fetch. */
    checkOverride?: () => Promise<SmartReaderGateResponse>;
}

export const SmartReaderRouteGate: React.FC<SmartReaderRouteGateProps> = ({ children, checkOverride }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const feature: SmartReaderFeature = SMART_READER_ROUTES[location.pathname] || 'smart-document-reader';
    const [response, setResponse] = useState<SmartReaderGateResponse | null>(null);

    useEffect(() => {
        let mounted = true;
        setResponse(null);
        const check = checkOverride ? checkOverride() : checkSmartReaderAccess(feature);
        check
            .then((r) => {
                if (mounted) setResponse(r);
            })
            .catch(() => {
                // Fail-closed: a rejected check denies rather than hangs.
                if (mounted) setResponse(evaluateSmartReaderGate(null, feature));
            });
        return () => {
            mounted = false;
        };
    }, [feature, checkOverride]);

    if (!response) {
        return (
            <div className="max-w-7xl mx-auto px-4 py-16 text-center text-gray-500" data-testid="smart-reader-checking">
                Checking your plan…
            </div>
        );
    }

    if (response.allowed) return <>{children}</>;

    return (
        <FeatureUpgradeModal
            isOpen
            testid="smart-reader-upgrade-modal"
            featureName="Smart Document Reader"
            icon="fas fa-file-lines"
            tier="enterprise"
            tierLabel="Enterprise"
            campaign="smart-reader-gate"
            reason={response.reason}
            valueProps={[
                'Upload leases, notices, and inspection docs — key facts extracted automatically.',
                'OCR + structured data in seconds, cross-referenced against your Texas compliance records.',
                'Export clean summaries straight into Evidence Hub for audit-ready files.',
            ]}
            roi={{
                incidentCostUsd: 250,
                hoursPerIncident: 3,
                incidentsLabel: 'Docs you would otherwise key in by hand, per month',
                basisNote: 'Basis: $250 avg. staff cost per manual doc-hassle incident, 3 hours recovered per doc. ROI is an estimate, not a guarantee.',
            }}
            onClose={() => navigate('/dashboard')}
        />
    );
};

/** Convenience: evaluate a tier synchronously for callers that already hold it. */
export { evaluateSmartReaderGate };
export * from './guard';
export default SmartReaderRouteGate;